// Os filtros do visualizador de conversas que chegam pelo URL, e a passagem do estado dele para os
// parametros do pedido da lista (D16 do plano do CSAT na pagina). A pagina de analise liga para aqui
// com o chat, o periodo pelo fecho e a estrela, o comentario ou o operador, e com o chat, o periodo
// pelo inicio e a voz (D14 do plano das mensagens de voz).

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
// A coluna csat_operator_id e `integer`: acima disto o backend responde 422.
const MAX_OPERATOR_ID = 2147483647;

// "2026-09-04" -> o instante da meia-noite LOCAL desse dia, em ISO. Os dois limites tem de usar a mesma
// base de tempo: mandar a data crua num e um instante local no outro desalinha-os pelo offset da zona.
const localMidnightIso = (yyyyMmDd) => new Date(`${yyyyMmDd}T00:00:00`).toISOString();

// O limite de cima do backend (created_to, resolved_to) e EXCLUSIVO, portanto "ate dia 4" manda a
// meia-noite do dia 5 para que o dia 4 inteiro conte.
const nextLocalMidnightIso = (yyyyMmDd) => {
  const d = new Date(`${yyyyMmDd}T00:00:00`);
  d.setDate(d.getDate() + 1);
  return d.toISOString();
};

// Um dia de calendario que existe, ou "". O Date aceita 2026-02-31 e passa-o a 3 de marco, e um filtro
// com essa data mostrava outro periodo; por isso o dia volta a ler-se do Date e tem de bater certo.
const calendarDay = (value) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value || "");
  if (!match) return "";
  const [year, month, day] = match.slice(1).map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  const exists = date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day;
  return exists ? value : "";
};

const ratingOf = (value) => (/^[1-5]$/.test(value || "") ? Number(value) : null);

const operatorIdOf = (value) => {
  if (!/^[1-9]\d{0,9}$/.test(value || "")) return null;
  const id = Number(value);
  return id <= MAX_OPERATOR_ID ? id : null;
};

// Comparacao explicita: um lookup num objeto passaria por `toString` e `__proto__` do prototipo.
const handedOffOf = (value) => (value === "true" ? true : value === "false" ? false : null);

// Os filtros de um URL do visualizador. Nunca rebenta: um valor que nao se le fica desligado e o resto
// le-se na mesma, porque nao ha ErrorBoundary e uma excecao aqui deixava a pagina em branco.
export const filtersFromSearch = (search) => {
  const params = new URLSearchParams(search);
  const closedFrom = calendarDay(params.get("closed_from"));
  const closedTo = calendarDay(params.get("closed_to"));
  const byClose = Boolean(closedFrom || closedTo);
  // Os dias pelo fecho (CSAT) ou pelo inicio (voz): com os dois no URL ganha o fecho, que e o que a
  // pagina de analise sempre mandou, e o periodo nunca mistura as duas bases.
  const from = byClose ? closedFrom : calendarDay(params.get("started_from"));
  const to = byClose ? closedTo : calendarDay(params.get("started_to"));
  const csatRating = ratingOf(params.get("rating"));
  const operatorId = operatorIdOf(params.get("operator"));
  return {
    chatId: UUID.test(params.get("chat") || "") ? params.get("chat") : "",
    periodBy: byClose ? "resolved" : "created",
    from,
    to,
    csatRating,
    // Com a estrela e o "com avaliacao" no mesmo URL, fica so a estrela: no painel sao um so grupo.
    csatRated: params.get("rated") === "1" && csatRating === null ? true : null,
    csatCommented: params.get("commented") === "1" ? true : null,
    operatorId,
    // O nome so serve a pastilha, e sem o id nao ha filtro nenhum por tras dela.
    operatorName: operatorId !== null && params.get("operator_name") ? params.get("operator_name") : null,
    handedOff: handedOffOf(params.get("handed_off")),
    // So o 1 liga o filtro: qualquer outro valor ignora-se, como o `rated` e o `commented`.
    voice: params.get("voice") === "1" ? true : null,
  };
};

// O estado inicial do visualizador a partir do URL: os valores com que o componente arranca. Sem
// parametros e exatamente o estado de antes (tudo desligado, sem `handed_off`). O `handed_off=true` e o
// botao "Transferidas" premido e o `false` e o "so com o assistente"; um periodo do URL fica como datas
// escolhidas ("custom"), pelo fecho (CSAT) ou pelo inicio (voz).
export const viewerStateFromSearch = (search) => {
  const url = filtersFromSearch(search);
  return {
    chatId: url.chatId,
    status: url.handedOff === true ? "handed_off" : "",
    assistantOnly: url.handedOff === false,
    period: url.from || url.to ? "custom" : "",
    periodBy: url.periodBy,
    from: url.from,
    to: url.to,
    csatRating: url.csatRating,
    csatRated: Boolean(url.csatRated),
    csatCommented: Boolean(url.csatCommented),
    operator: url.operatorId === null ? null : { id: url.operatorId, name: url.operatorName },
    voice: url.voice === true,
  };
};

// O estado do visualizador nos parametros do conversationsApi.list. Um booleano desligado vai como null
// e nunca como false: `csat_commented=false` tirava da lista diaria todas as conversas com comentario,
// e `handed_off=false` todas as que passaram a um operador.
//
// O botao "Transferidas" nao pergunta pelo `status`: nada no backend escreve
// `c.status = 'handed_off'`, portanto esse filtro nao devolvia nunca uma linha. O que a lista pinta a
// ambar e o `handed_off`, que o backend define como "chegou a fase humana" (o
// _REACHED_HUMAN_PHASE_SQL), e e por esse que se pergunta, para o filtro e o ponto dizerem a mesma
// coisa por construcao. Com qualquer estado escolhido o "so com o assistente" nao conta.
export const listFilters = ({ chatId, status, assistantOnly, periodBy, from, to, csatRating, csatRated, csatCommented, operatorId, voice }) => {
  const start = from ? localMidnightIso(from) : null;
  const end = to ? nextLocalMidnightIso(to) : null;
  const byClose = periodBy === "resolved";
  const handedOff = status === "handed_off" ? true : assistantOnly && !status ? false : null;
  return {
    chatId: chatId || null,
    status: status === "handed_off" ? null : status || null,
    handedOff,
    createdFrom: byClose ? null : start,
    createdTo: byClose ? null : end,
    resolvedFrom: byClose ? start : null,
    resolvedTo: byClose ? end : null,
    csatRating: csatRating || null,
    csatRated: csatRated ? true : null,
    csatCommented: csatCommented ? true : null,
    csatOperatorId: operatorId || null,
    // Como os outros: desligado vai como null, e um `with_voice=false` tirava as conversas com voz.
    withVoice: voice ? true : null,
  };
};

// A ligacao da pagina de analise para o visualizador: o chat e o periodo da pagina ({from, to}, dias
// inclusivos), sempre pelo fecho, que e como a pagina conta o CSAT. O operador leva sempre `rated=1`: o
// csat_operator_id grava-se no fecho de toda a conversa da fase humana, avaliada ou nao, e sem ele a
// lista trazia mais conversas do que o numero da pagina.
export const csatConversationsLink = ({ chatId, period, rating = null, commented = false, operator = null, handedOff = null }) => {
  const params = new URLSearchParams({ chat: chatId, closed_from: period.from, closed_to: period.to });
  if (rating) params.set("rating", rating);
  if (operator) params.set("rated", "1");
  if (commented) params.set("commented", "1");
  if (operator) {
    params.set("operator", operator.id);
    if (operator.name) params.set("operator_name", operator.name);
  }
  if (handedOff !== null) params.set("handed_off", handedOff);
  return `/conversations?${params}`;
};

// A ligacao do cartao das mensagens de voz para o visualizador: o chat e o periodo da pagina ({from, to},
// dias inclusivos), pelo INICIO da conversa, que e como o `conversations_with_voice` conta, e o filtro da
// voz. Leva sempre o chat.
export const voiceConversationsLink = ({ chatId, period }) => {
  const params = new URLSearchParams({ chat: chatId, started_from: period.from, started_to: period.to, voice: "1" });
  return `/conversations?${params}`;
};
